import { FC, createContext, useContext, useMemo } from "react";
import {
  useQuery as reactQueryUseQuery,
  useMutation as reactQueryUseMutation,
  QueryClientProvider as ReactQueryQueryClientProvider,
  UseQueryResult as ReactQueryUseQueryResult,
  UseMutationResult as ReactQueryUseMutationResult,
  UseMutationOptions as ReactQueryUseMutationOptions,
  QueryClient as ReactQueryQueryClient,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { captureException } from "@sentry/nextjs";
import {
  IDataFetcherResponseError,
  IUseQueryContext,
  MutationKey,
  QueryClientProviderProps,
  QueryKey,
  UseQueryOptionsType,
} from "./types";
import { dataFetcher, getAbsoluteUrl, normalizeQueryKey } from "./utils";

const UseQueryContext = createContext<IUseQueryContext>({});

export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  queryKey: QueryKey,
  options: Omit<
    UseQueryOptionsType<TQueryFnData, TError, TData>,
    "queryKey" | "queryFn"
  > = {},
): ReactQueryUseQueryResult<TData, IDataFetcherResponseError<TError>> {
  const { baseUrl, getHeaders, onError } = useContext(UseQueryContext);

  return reactQueryUseQuery({
    queryKey: normalizeQueryKey(queryKey),
    refetchOnWindowFocus: false,
    async queryFn({ signal }) {
      const { url, method, params } =
        typeof queryKey === "string"
          ? {
              url: queryKey,
              method: "GET",
              params: {},
            }
          : queryKey;

      const absoluteUrl = getAbsoluteUrl(url, { baseUrl, params });
      const { success, error, data } = await dataFetcher(absoluteUrl, {
        method,
        headers: await getHeaders?.(),
        onError,
        signal,
      });

      if (success) {
        return data;
      }

      throw error;
    },
    ...options,
  });
}

export function useMutation<
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  mutationKey: MutationKey,
  options?: Omit<
    ReactQueryUseMutationOptions<
      TData,
      IDataFetcherResponseError<TError>,
      TVariables,
      TContext
    >,
    "mutationFn"
  >,
): ReactQueryUseMutationResult<
  TData,
  IDataFetcherResponseError<TError>,
  TVariables,
  TContext
> {
  const { baseUrl, getHeaders, onError } = useContext(UseQueryContext);

  return reactQueryUseMutation({
    mutationKey: normalizeQueryKey(mutationKey),
    async mutationFn(body: any) {
      const { url, method, params } = mutationKey;
      const absoluteUrl = getAbsoluteUrl(url, { baseUrl, params });
      const { success, data, error } = await dataFetcher(absoluteUrl, {
        method,
        headers: await getHeaders?.(),
        body,
        onError,
      });

      if (success) {
        return data;
      }

      throw error;
    },
    ...options,
  });
}

// status 0 = fetch itself threw (no HTTP response)
export function isNetworkError(error: unknown): boolean {
  return (error as IDataFetcherResponseError | undefined)?.status === 0;
}

export function shouldRetry(failureCount: number, error: unknown): boolean {
  return failureCount < 2 && isNetworkError(error);
}

// fires once per query/mutation, after retries are exhausted
export function captureError(
  { cause }: IDataFetcherResponseError,
  extra: Record<string, unknown>,
): void {
  if (!(cause instanceof Error)) {
    return;
  }

  captureException(cause, {
    extra,
    tags: { offline: typeof navigator !== "undefined" && !navigator.onLine },
  });
}

const queryClient = new ReactQueryQueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      captureError(error, { queryKey: query.queryKey }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      captureError(error, { mutationKey: mutation.options.mutationKey }),
  }),
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      retryDelay: (attempt) => 1000 * (attempt + 1),
    },
  },
});

export const QueryClientProvider: FC<QueryClientProviderProps> = ({
  baseUrl,
  getHeaders,
  onError,
  ...props
}) => {
  const contextValue = useMemo(() => {
    return {
      baseUrl,
      getHeaders,
      onError,
    };
  }, [baseUrl, getHeaders, onError]);

  return (
    <UseQueryContext.Provider value={contextValue}>
      <ReactQueryQueryClientProvider client={queryClient} {...props} />
    </UseQueryContext.Provider>
  );
};
